// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Click-to-define glossary. `.glossary-link[data-term]` spans anywhere in the UI
 * pop a small definition tooltip; Escape or an outside click dismisses it.
 */

export const GLOSSARY: Record<string, string> = {
  'end-to-end encrypted':
    'Scrambled on your device and only unscrambled on the other person’s device. Nothing in between — no relay, no server — can read it.',
  'full mesh':
    'Every participant connects directly to every other participant. There is no central server mixing the call, so your audio and video never pass through one.',
  'dtls-srtp':
    'The encryption WebRTC mandates for every peer-to-peer media stream. Your camera and mic are encrypted on the wire between the two browsers.',
  webrtc:
    'The browser technology for real-time voice, video and data directly between devices — the same tech behind most in-browser calling.',
  'signalling relay':
    'A public service used once, at the start, to help two browsers find each other. It only shepherds the handshake — which we encrypt with your passphrase — and never carries your call.',
  stun:
    'A public helper that tells your browser how it appears to the internet, so two devices behind home routers can find a path to each other.',
  'symmetric nat':
    'A stricter type of home/office router. If both people are behind one, browsers sometimes can’t find a direct path without a relay — which we deliberately don’t run.',
  sas:
    'Short Authentication String — four words derived from your shared passphrase. If both sides see the same four words, no impostor slipped into the middle.',
  passphrase:
    'The shared secret for the room. It’s used to encrypt the handshake and to derive the four verification words. It lives only in the link’s hash and never reaches any server.',
  'ice candidate':
    'A possible network path between two devices. Browsers gather several and pick one that works.',
};

let tip: HTMLElement | null = null;

export function initGlossary(root: HTMLElement = document.body): void {
  root.addEventListener('click', (e) => {
    const target = (e.target as HTMLElement).closest('.glossary-link') as HTMLElement | null;
    if (!target) {
      hide();
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    const term = target.dataset.term?.toLowerCase() ?? '';
    const def = GLOSSARY[term];
    if (!def) return;
    show(target, term, def);
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') hide();
  });
  window.addEventListener('scroll', hide, true);
}

function show(anchor: HTMLElement, term: string, def: string): void {
  hide();
  tip = document.createElement('div');
  tip.className = 'glossary-tip';
  tip.innerHTML = `<div class="glossary-term"></div><div class="glossary-def"></div>`;
  (tip.querySelector('.glossary-term') as HTMLElement).textContent = term;
  (tip.querySelector('.glossary-def') as HTMLElement).textContent = def;
  document.body.appendChild(tip);
  const r = anchor.getBoundingClientRect();
  const tr = tip.getBoundingClientRect();
  let left = r.left;
  if (left + tr.width > window.innerWidth - 12) left = window.innerWidth - tr.width - 12;
  if (left < 12) left = 12;
  let top = r.bottom + 8;
  if (top + tr.height > window.innerHeight - 12) top = r.top - tr.height - 8;
  tip.style.left = `${left}px`;
  tip.style.top = `${Math.max(12, top)}px`;
}

function hide(): void {
  if (tip) {
    tip.remove();
    tip = null;
  }
}
